import { Head, router } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { AlertTriangle, Calendar, CheckCircle2, Download, Search, ShieldAlert, Wallet } from 'lucide-react'
import Pagination from '@/components/pagination.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Card } from '@/components/ui/card.tsx'
import { Button } from '@/components/ui/button.tsx'

interface PaiementAuditRow {
  id: string
  collected_at?: string | null
  community_name?: string | null
  tax_payer_name?: string | null
  tax_name?: string | null
  tax_rule_code?: string | null
  collect_session_code?: string | null
  agent_name?: string | null
  device_name?: string | null
  payment_method?: string | null
  amount: number
  sync: boolean
  audit_status: 'balanced' | 'warning' | 'critical'
}

interface PaginationType {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
  next_page: number | null
  prev_page: number | null
}

interface IndexProps {
  payment_audits: PaiementAuditRow[]
  pagination: PaginationType
  stats: {
    total_payments: number
    total_amount: number
    unique_taxpayers: number
    balanced_payments: number
    warning_payments: number
    critical_payments: number
  }
  filters: {
    term: string
    community_id: string
    payment_method: string
    audit_status: string
    date_from: string
    date_to: string
  }
  filter_options: {
    communities: Array<{ id: string; name: string }>
    payment_methods: string[]
    audit_statuses: string[]
  }
}

const formatMoney = (amount: number) => `${amount.toLocaleString()} FCFA`

export default function Index({ payment_audits, pagination, stats, filters, filter_options }: IndexProps) {
  const hasInitialized = useRef(false)
  const fromDateInputRef = useRef<HTMLInputElement>(null)
  const toDateInputRef = useRef<HTMLInputElement>(null)
  const [term, setTerm] = useState(filters.term)
  const [communityId, setCommunityId] = useState(filters.community_id)
  const [paymentMethod, setPaiementMethod] = useState(filters.payment_method)
  const [auditStatus, setAuditStatus] = useState(filters.audit_status)
  const [dateFrom, setDateFrom] = useState(filters.date_from)
  const [dateTo, setDateTo] = useState(filters.date_to)

  useEffect(() => {
    setTerm(filters.term)
    setCommunityId(filters.community_id)
    setPaiementMethod(filters.payment_method)
    setAuditStatus(filters.audit_status)
    setDateFrom(filters.date_from)
    setDateTo(filters.date_to)
  }, [filters.audit_status, filters.community_id, filters.date_from, filters.date_to, filters.payment_method, filters.term])

  const query = useMemo(() => ({
    term,
    community_id: communityId,
    payment_method: paymentMethod,
    audit_status: auditStatus,
    date_from: dateFrom,
    date_to: dateTo
  }), [auditStatus, communityId, dateFrom, dateTo, paymentMethod, term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (
      term === filters.term &&
      communityId === filters.community_id &&
      paymentMethod === filters.payment_method &&
      auditStatus === filters.audit_status &&
      dateFrom === filters.date_from &&
      dateTo === filters.date_to
    ) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get('/admin/payment_audits', { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [auditStatus, communityId, dateFrom, dateTo, filters.audit_status, filters.community_id, filters.date_from, filters.date_to, filters.payment_method, filters.term, paymentMethod, query, term])

  const openDatePicker = (inputRef: RefObject<HTMLInputElement | null>) => {
    const input = inputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null
    input?.showPicker?.()
  }

  const formatAuditStatus = (value: string) => {
    if (value === 'balanced') return 'Conforme'
    if (value === 'warning') return 'Avertissement'
    if (value === 'critical') return 'Critique'
    return value
  }

  const exportCsv = () => {
    const params = new URLSearchParams()
    if (term) params.set('term', term)
    if (communityId) params.set('community_id', communityId)
    if (paymentMethod) params.set('payment_method', paymentMethod)
    if (auditStatus) params.set('audit_status', auditStatus)
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)

    const queryString = params.toString()
    const url = `/admin/payment_audits.csv${queryString ? `?${queryString}` : ''}`
    window.location.assign(url)
  }

  return (
    <>
      <Head title="Audit des paiements" />

      <div className="space-y-6 p-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Audit des paiements</h2>
            <p className="text-sm text-muted-foreground">Contrôle qualité des paiements collectés et anomalies de transaction.</p>
          </div>
          <Button type="button" variant="outline" className="gap-2" onClick={exportCsv}>
            <Download className="h-4 w-4" />
            Exporter CSV
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total paiements</p>
                <p className="mt-1 text-2xl font-bold text-foreground">{stats.total_payments}</p>
              </div>
              <Wallet className="h-7 w-7 text-primary" />
            </div>
          </Card>
          <Card className="border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conformes</p>
                <p className="mt-1 text-2xl font-bold text-success">{stats.balanced_payments}</p>
              </div>
              <CheckCircle2 className="h-7 w-7 text-success" />
            </div>
          </Card>
          <Card className="border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avertissements</p>
                <p className="mt-1 text-2xl font-bold text-warning">{stats.warning_payments}</p>
              </div>
              <AlertTriangle className="h-7 w-7 text-warning" />
            </div>
          </Card>
          <Card className="border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critiques</p>
                <p className="mt-1 text-2xl font-bold text-destructive">{stats.critical_payments}</p>
              </div>
              <ShieldAlert className="h-7 w-7 text-destructive" />
            </div>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-border bg-card p-4">
            <p className="text-sm text-muted-foreground">Montant total collecté</p>
            <p className="mt-1 text-xl font-semibold text-foreground">{formatMoney(stats.total_amount || 0)}</p>
          </Card>
          <Card className="border-border bg-card p-4">
            <p className="text-sm text-muted-foreground">Contribuables concernés</p>
            <p className="mt-1 text-xl font-semibold text-foreground">{stats.unique_taxpayers || 0}</p>
          </Card>
        </div>

        <Card className="border-border bg-card p-6">
          <div className="grid gap-4 md:grid-cols-6">
            <div>
              <label className="text-xs text-muted-foreground">Recherche</label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-10"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Contribuable, taxe, session..."
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Communauté</label>
              <select
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                value={communityId}
                onChange={(e) => setCommunityId(e.target.value)}
              >
                <option value="">Toutes</option>
                {filter_options.communities.map((community) => (
                  <option key={community.id} value={community.id}>{community.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Méthode</label>
              <select
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                value={paymentMethod}
                onChange={(e) => setPaiementMethod(e.target.value)}
              >
                <option value="">Toutes</option>
                {filter_options.payment_methods.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Audit</label>
              <select
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                value={auditStatus}
                onChange={(e) => setAuditStatus(e.target.value)}
              >
                <option value="">Tous</option>
                {filter_options.audit_statuses.map((statusValue) => (
                  <option key={statusValue} value={statusValue}>{formatAuditStatus(statusValue)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Du</label>
              <div className="relative mt-1">
                <Input
                  ref={fromDateInputRef}
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-secondary"
                  onClick={() => openDatePicker(fromDateInputRef)}
                >
                  <Calendar className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Au</label>
              <div className="relative mt-1">
                <Input
                  ref={toDateInputRef}
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-secondary"
                  onClick={() => openDatePicker(toDateInputRef)}
                >
                  <Calendar className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="border-border bg-card">
          <div className="overflow-hidden rounded-lg">
            <table className="w-full">
              <thead className="border-b border-border bg-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Paiement</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Contribuable / Taxe</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Session / Agent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Montant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Méthode</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Audit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payment_audits.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-sm text-muted-foreground" colSpan={6}>
                      Aucun paiement trouvé pour ces filtres.
                    </td>
                  </tr>
                )}
                {payment_audits.map((payment) => (
                  <tr key={payment.id} className="hover:bg-secondary/50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-foreground">{payment.id}</p>
                      <p className="text-xs text-muted-foreground">
                        {payment.collected_at ? new Date(payment.collected_at).toLocaleString('fr-FR') : '-'}
                      </p>
                      <p className="text-xs text-muted-foreground">{payment.community_name || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{payment.tax_payer_name || '-'}</p>
                      <p className="text-xs text-muted-foreground">{payment.tax_name || '-'}</p>
                      <p className="text-xs text-muted-foreground">{payment.tax_rule_code || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{payment.collect_session_code || '-'}</p>
                      <p className="text-xs text-muted-foreground">{payment.agent_name || '-'}</p>
                      <p className="text-xs text-muted-foreground">{payment.device_name || '-'}</p>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{formatMoney(payment.amount || 0)}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{payment.payment_method || '-'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                          payment.audit_status === 'balanced'
                            ? 'bg-success/10 text-success'
                            : payment.audit_status === 'warning'
                              ? 'bg-warning/10 text-warning'
                              : 'bg-destructive/10 text-destructive'
                        }`}
                      >
                        {payment.audit_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          basePath="/admin/payment_audits"
          query={query}
        />
      </div>
    </>
  )
}
